import { POS_PERMISSIONS } from '@matix/types';
import { useCart, type OrderMode } from '../../store/cart';
import { useTenantConfig } from '../../store/tenantConfig';
import { useCannot } from '../../lib/permissions';
import { useLang } from '../../lib/language';
import {
  ShoppingCartIcon, UsersIcon, TruckIcon, PackageIcon, CreditCardIcon,
} from '../../components/icons';

/**
 * Top-level order-mode pill group. Sits in the topbar next to the New Sale
 * title, and is the ONLY order-type control in the cashier shell.
 *
 * Five modes: Walk-In / Dine-in / Delivery / Pickup / Membership. A pill shows
 * only when the tenant enabled that order type in admin → Settings → POS
 * Settings (mstr_ConfigurationParameter → IsTakeAway / IsDineIn / IsDelivery /
 * IsPickUp / IsMembership) AND the signed-in cashier does not hold the matching
 * "Not Allow …" permission. Dine-in additionally needs IsRestaurant. The
 * tenant's default order type decides which pill a fresh sale opens on.
 *
 * Behaviour:
 *   - Switching mode does NOT clear the cart or the delivery draft. That
 *     way a cashier can flip back and forth without losing typing.
 *   - When mode === 'dine-in', a table must be attached before charging
 *     (enforced in CartPanel).
 *   - When mode === 'delivery', the cart shows the delivery banner and
 *     the Charge button opens the Delivery modal instead of the regular
 *     payment modal.
 */

const MODES: Array<{
  value: OrderMode;
  label: string;
  icon: (props: { size?: number }) => JSX.Element;
}> = [
  { value: 'takeaway', label: 'Walk-In', icon: ShoppingCartIcon },
  { value: 'dine-in', label: 'Dine-in', icon: UsersIcon },
  { value: 'delivery', label: 'Delivery', icon: TruckIcon },
  { value: 'pickup', label: 'Pickup', icon: PackageIcon },
  { value: 'membership', label: 'Membership', icon: CreditCardIcon },
];

const MODE_LABELS: Record<OrderMode, { en: string; ar: string }> = {
  takeaway: { en: 'Walk-In', ar: 'محلي' },
  'dine-in': { en: 'Dine-in', ar: 'صالة' },
  delivery: { en: 'Delivery', ar: 'توصيل' },
  pickup: { en: 'Pickup', ar: 'استلام' },
  membership: { en: 'Membership', ar: 'عضوية' },
};

export function OrderModeSwitch() {
  const { t } = useLang();
  const orderMode = useCart((s) => s.orderMode);
  const setOrderMode = useCart((s) => s.setOrderMode);
  const loaded = useTenantConfig((s) => s.loaded);
  const isRestaurant = useTenantConfig((s) => s.isRestaurant);
  const isTakeAway = useTenantConfig((s) => s.isTakeAway);
  const isDelivery = useTenantConfig((s) => s.isDelivery);
  const isDineIn = useTenantConfig((s) => s.isDineIn);
  const isPickUp = useTenantConfig((s) => s.isPickUp);
  const isMembership = useTenantConfig((s) => s.isMembership);

  // Each pill is gated by its admin order-type flag (Settings → POS Settings).
  // Dine-in additionally requires the tenant to be a restaurant — without
  // tables there is nothing to attach a dine-in order to.
  const enabled: Record<OrderMode, boolean> = {
    takeaway: isTakeAway,
    delivery: isDelivery,
    'dine-in': isRestaurant && isDineIn,
    pickup: isPickUp,
    membership: isMembership,
  };

  // Per-user negative gates (legacy "Not Allow ..." permissions): holding one
  // BLOCKS that order type for the cashier, on top of the tenant flag. Driven
  // by the group permissions assigned in admin → Groups.
  const blocked: Record<OrderMode, boolean> = {
    takeaway: useCannot(POS_PERMISSIONS.NotAllowTakeAway),
    delivery: useCannot(POS_PERMISSIONS.NotAllowDelivery),
    'dine-in': useCannot(POS_PERMISSIONS.NotAllowDineIn),
    pickup: useCannot(POS_PERMISSIONS.NotAllowPickup),
    membership: useCannot(POS_PERMISSIONS.NotAllowMembership),
  };

  const modes = MODES.filter((m) => enabled[m.value] && !blocked[m.value]);

  // Until the tenant flags land, the store still holds its optimistic defaults
  // (takeaway/delivery/dine-in ON). Rendering those would flash order types the
  // admin has switched off, so hold the strip back for the one boot request.
  // `loaded` is set even when the fetch fails, so this never sticks.
  if (!loaded) return <div className="order-mode-switch" aria-busy="true" />;

  return (
    <div className="order-mode-switch" role="tablist" aria-label={t({ en: 'Order mode', ar: 'نوع الطلب' })}>
      {modes.map(({ value, icon: Icon }) => (
        <button
          key={value}
          type="button"
          role="tab"
          aria-selected={orderMode === value}
          className={`order-mode-btn ${orderMode === value ? 'active' : ''}`}
          onClick={() => setOrderMode(value)}
        >
          <Icon size={16} />
          {t(MODE_LABELS[value])}
        </button>
      ))}
    </div>
  );
}
